import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Body1,
  Button,
  Card,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Textarea,
  Title3,
} from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  CalendarRegular,
  PersonRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  CheckmarkRegular,
} from '@fluentui/react-icons';
import { format, isSameMonth, isSameDay, isToday } from 'date-fns';
import { transformrBrand, brandFonts } from '@/theme/transformrTheme';
import { useBookingCalendar, localDateKey } from '@/features/booking/hooks/useBookingCalendar';
import { useAuth } from '@/hooks/useAuth';
import {
  BookingConfirmation,
  getAvailability,
  createBooking,
} from '@/features/booking';
import type {
  TimeSlot,
  AvailabilityResponse,
  BookingResponse,
  BookingStep,
} from '@/features/booking';
import { listReports } from '@/features/discovery/services/discoveryApi';
import type { StoredReport } from '@/features/discovery/types/discovery.types';
import { reportTitle } from '@/features/discovery/utils/reportTitle';
import { PageBanner } from '@/components/common/PageBanner';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXXL,
  },
  // Banner spans full width; the single-column content below sits at 70%, left.
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXXL,
    maxWidth: '70%',
    // 70% only above 1600px; at 1600px and below (incl. mobile) stretch to 100%.
    '@media (max-width: 1600px)': { maxWidth: '100%' },
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalS,
  },
  title: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    margin: 0,
    color: tokens.colorNeutralForeground1,
  },
  stepIndicator: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  stepDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralStroke2,
  },
  stepDotActive: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandForeground1,
  },
  stepLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginLeft: tokens.spacingHorizontalXS,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalM,
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXS,
  },
  notesArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalS,
  },
  summaryCard: {
    padding: tokens.spacingHorizontalXXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalM,
  },
  summaryRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'flex-start',
  },
  summaryLabel: {
    fontWeight: tokens.fontWeightSemibold,
    minWidth: '100px',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  summaryValue: {
    color: tokens.colorNeutralForeground1,
  },
  // ── Dossier "Book a Meeting" chrome ──────────────────────────────────────
  stepper: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
  },
  stepperStep: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: transformrBrand.grey700,
  },
  stepperStepActive: {
    color: transformrBrand.navy,
  },
  stepperDot: {
    width: '8px',
    height: '8px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: transformrBrand.grey300,
  },
  stepperDotActive: {
    backgroundColor: transformrBrand.greenAlt,
  },
  stepperLine: {
    width: '40px',
    height: '1px',
    backgroundColor: transformrBrand.grey300,
  },
  // Numbered row: big green number + content column.
  numberedRow: {
    display: 'grid',
    gridTemplateColumns: '4.8rem 1fr',
    columnGap: tokens.spacingHorizontalL,
    alignItems: 'start',
    paddingTop: tokens.spacingVerticalXXL,
    borderTop: `1px solid ${transformrBrand.grey200}`,
  },
  num: {
    fontFamily: brandFonts.heading,
    fontSize: '2.875rem', // 46px — larger than any fontSize token
    fontWeight: tokens.fontWeightMedium,
    letterSpacing: '-0.04em',
    lineHeight: '1',
    color: transformrBrand.greenAlt,
  },
  rowContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minWidth: 0,
  },
  rowEyebrow: {
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase100,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: transformrBrand.grey700,
  },
  rowTitle: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
    color: transformrBrand.navy,
    margin: 0,
  },
  rowSubtitle: {
    color: transformrBrand.grey700,
    marginTop: `-${tokens.spacingVerticalS}`,
  },
  // Consultant card.
  consultantCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    backgroundColor: transformrBrand.grey100,
    border: `1px solid ${transformrBrand.grey200}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  consultant: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  consultantAv: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '46px',
    height: '46px',
    flexShrink: 0,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: transformrBrand.greenAlt,
    color: transformrBrand.navy,
  },
  consultantName: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: transformrBrand.navy,
  },
  consultantRole: {
    fontSize: tokens.fontSizeBase200,
    color: transformrBrand.grey700,
  },
  consultantFoot: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase100,
    letterSpacing: '0.04em',
    color: transformrBrand.grey700,
  },
  // Row 1 — calendar beside the consultant.
  dateGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 70fr) minmax(0, 30fr)',
    gap: tokens.spacingHorizontalXL,
    alignItems: 'start',
    '@media (max-width: 760px)': { gridTemplateColumns: '1fr' },
  },
  calCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: transformrBrand.white,
    border: `1px solid ${transformrBrand.grey200}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  calHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  calTitle: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: transformrBrand.navy,
  },
  dowRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: tokens.spacingHorizontalXS,
  },
  dow: {
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase100,
    color: transformrBrand.grey700,
    paddingLeft: tokens.spacingHorizontalXXS,
  },
  daysGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: tokens.spacingHorizontalXS,
  },
  day: {
    display: 'flex',
    alignItems: 'flex-start',
    minHeight: '3.25rem', // 52px cell
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${transformrBrand.grey200}`,
    backgroundColor: transformrBrand.grey100,
    color: transformrBrand.grey300,
    fontSize: tokens.fontSizeBase200,
    cursor: 'default',
  },
  dayOut: {
    border: '1px solid transparent',
    backgroundColor: 'transparent',
  },
  dayAvail: {
    backgroundColor: transformrBrand.greenSoft,
    border: `1px solid ${transformrBrand.greenAlt}`,
    color: transformrBrand.navy,
    cursor: 'pointer',
    ':hover': { backgroundColor: transformrBrand.greenAlt, color: transformrBrand.navy },
  },
  daySelected: {
    backgroundColor: transformrBrand.greenAlt,
    border: `1px solid ${transformrBrand.navy}`,
    color: transformrBrand.navy,
  },
  // Row 2 — time cards.
  timesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(8.5rem, 1fr))',
    gap: tokens.spacingHorizontalS,
  },
  timeChip: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    backgroundColor: transformrBrand.white,
    border: `1px solid ${transformrBrand.grey200}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': {
      border: `1px solid ${transformrBrand.greenAlt}`,
      backgroundColor: transformrBrand.greenSoft,
    },
  },
  timeChipSelected: {
    border: `1px solid ${transformrBrand.greenAlt}`,
    backgroundColor: transformrBrand.greenSoft,
    ':hover': {
      border: `1px solid ${transformrBrand.greenAlt}`,
      backgroundColor: transformrBrand.greenSoft,
    },
  },
  timeChipH: {
    fontFamily: brandFonts.heading,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: transformrBrand.navy,
  },
  timeChipD: {
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase100,
    color: transformrBrand.grey700,
  },
  // Row 3 — report list rows (DevOps-style).
  repList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    // Show ~5 rows (≈48px each incl. gap), then scroll.
    maxHeight: '15rem',
    overflowY: 'auto',
    paddingRight: tokens.spacingHorizontalXS,
  },
  report: {
    display: 'grid',
    gridTemplateColumns: '1.25rem 1fr auto',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingHorizontalM,
    backgroundColor: transformrBrand.white,
    border: `1px solid ${transformrBrand.grey200}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': {
      border: `1px solid ${transformrBrand.greenAlt}`,
      backgroundColor: transformrBrand.greenSoft,
    },
  },
  reportSelected: {
    border: `1px solid ${transformrBrand.greenAlt}`,
    backgroundColor: transformrBrand.greenSoft,
  },
  repCheck: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.25rem',
    height: '1.25rem',
    borderRadius: tokens.borderRadiusSmall,
    border: `1px solid ${transformrBrand.grey300}`,
    backgroundColor: transformrBrand.white,
    color: transformrBrand.white,
  },
  repCheckOn: {
    border: `1px solid ${transformrBrand.greenAlt}`,
    backgroundColor: transformrBrand.greenAlt,
    color: transformrBrand.navy,
  },
  repBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  repName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: transformrBrand.navy,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  repSub: {
    fontSize: tokens.fontSizeBase200,
    color: transformrBrand.grey700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  repDate: {
    fontFamily: brandFonts.mono,
    fontSize: tokens.fontSizeBase100,
    color: transformrBrand.grey700,
  },
});


export function BookingPage() {
  const styles = useStyles();
  const { getAccessToken } = useAuth();

  const [step, setStep] = useState<BookingStep>('select-time');
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [isLoadingSlots, setIsLoadingSlots] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bookingResult, setBookingResult] = useState<BookingResponse | null>(null);
  const [reports, setReports] = useState<StoredReport[]>([]);
  const [, setIsLoadingReports] = useState(true);

  // Load availability and reports on mount (single token acquisition to avoid MSAL race)
  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const token = await getAccessToken();
        if (!token || cancelled) return;

        // Fetch availability and reports in parallel, sharing the token
        const [availResult, reportsResult] = await Promise.allSettled([
          getAvailability(token),
          listReports(token),
        ]);

        if (cancelled) return;

        if (availResult.status === 'fulfilled') {
          setAvailability(availResult.value);
        } else {
          setError('Failed to load available times. Please try again.');
        }

        if (reportsResult.status === 'fulfilled') {
          const sorted = [...reportsResult.value.reports]
            .sort((a, b) => {
              if (!a.storedAt) return 1;
              if (!b.storedAt) return -1;
              return new Date(b.storedAt).getTime() - new Date(a.storedAt).getTime();
            })
            .slice(0, 20);
          setReports(sorted);
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load available times. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSlots(false);
          setIsLoadingReports(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [getAccessToken]);

  const refreshAvailability = useCallback(async () => {
    setIsLoadingSlots(true);
    setError(null);
    try {
      const token = await getAccessToken();
      if (!token) return;
      const result = await getAvailability(token);
      setAvailability(result);
    } catch {
      setError('Failed to refresh available times.');
    } finally {
      setIsLoadingSlots(false);
    }
  }, [getAccessToken]);

  const handleConfirm = useCallback(async () => {
    if (!selectedSlot) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const result = await createBooking(
        {
          slotStart: selectedSlot.start,
          reportIds: selectedReportIds,
          notes,
        },
        token
      );
      setBookingResult(result);
      setStep('success');
    } catch (err: unknown) {
      const apiErr = err as { statusCode?: number; message?: string };
      if (apiErr.statusCode === 409) {
        setError(
          'This time slot was just taken by someone else. Please choose another.'
        );
        setStep('select-time');
        setSelectedSlot(null);
        await refreshAvailability();
      } else {
        setError(apiErr.message || 'Failed to create booking. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedSlot, selectedReportIds, notes, getAccessToken, refreshAvailability]);

  const timezone = availability?.timezone || 'Australia/Sydney';

  // Calendar state (month grid + selected day) for the Dossier-style rows.
  const cal = useBookingCalendar(availability?.slots || [], timezone);
  const MAX_REPORTS = 5;
  const handleReportToggle = useCallback((analysisId: string) => {
    setSelectedReportIds((prev) =>
      prev.includes(analysisId)
        ? prev.filter((id) => id !== analysisId)
        : prev.length < MAX_REPORTS
          ? [...prev, analysisId]
          : prev,
    );
  }, []);
  const reportDateFormatter = useMemo(
    () => new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }),
    [],
  );

  const timeFormatter = new Intl.DateTimeFormat('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone,
  });

  return (
    <div className={styles.page}>
      {/* Header */}
      <PageBanner
        kicker="Talk to us"
        title="Book a Meeting"
        subtitle={step === 'success'
          ? 'Your meeting is confirmed.'
          : 'Choose a time to discuss your Readiness results with a Transformr consultant.'}
        icon={<CalendarRegular fontSize={14} />}
      />
      <div className={styles.content}>
      {/* Stepper */}
      {step !== 'success' && (
        <div className={styles.stepper}>
          <span className={mergeClasses(
            styles.stepperStep,
            step === 'select-time' && styles.stepperStepActive,
          )}>
            <span className={mergeClasses(
              styles.stepperDot,
              step === 'select-time' && styles.stepperDotActive,
            )} />
            Choose a Time
          </span>
          <span className={styles.stepperLine} />
          <span className={mergeClasses(
            styles.stepperStep,
            step === 'confirm' && styles.stepperStepActive,
          )}>
            <span className={mergeClasses(
              styles.stepperDot,
              step === 'confirm' && styles.stepperDotActive,
            )} />
            Confirm Details
          </span>
        </div>
      )}

      {/* Error bar */}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* Step 1: Select Time */}
      {step === 'select-time' && (
        <>
          {/* 01 — choose a date (calendar + consultant) */}
          <div className={styles.numberedRow}>
            <div className={styles.num}>01</div>
            <div className={styles.rowContent}>
              <span className={styles.rowEyebrow}>Schedule · choose a date</span>
              <h2 className={styles.rowTitle}>Pick a day that works</h2>
              <Body1 className={styles.rowSubtitle}>
                Green days have open consultant availability.
              </Body1>

              {isLoadingSlots ? (
                <Spinner size="large" label="Loading available times..." />
              ) : (availability?.slots?.length ?? 0) === 0 ? (
                <MessageBar intent="warning">
                  <MessageBarBody>
                    No available slots in the next few days — please check back later.
                  </MessageBarBody>
                </MessageBar>
              ) : (
                <div className={styles.dateGrid}>
                  <div className={styles.calCard}>
                    <div className={styles.calHead}>
                      <Button appearance="subtle" icon={<ChevronLeftRegular />} onClick={cal.prevMonth} aria-label="Previous month" />
                      <span className={styles.calTitle}>{format(cal.viewMonth, 'MMMM yyyy')}</span>
                      <Button appearance="subtle" icon={<ChevronRightRegular />} onClick={cal.nextMonth} aria-label="Next month" />
                    </div>
                    <div className={styles.dowRow}>
                      {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => (
                        <span key={d} className={styles.dow}>{d}</span>
                      ))}
                    </div>
                    <div className={styles.daysGrid}>
                      {cal.calendarDays.map((day) => {
                        const inMonth = isSameMonth(day, cal.viewMonth);
                        const hasSlots = cal.slotsByDate.has(localDateKey(day));
                        const isSel = cal.selectedDate !== null && isSameDay(day, cal.selectedDate);
                        return (
                          <button
                            key={localDateKey(day)}
                            type="button"
                            className={mergeClasses(
                              styles.day,
                              !inMonth && styles.dayOut,
                              hasSlots && styles.dayAvail,
                              isSel && styles.daySelected,
                            )}
                            onClick={hasSlots ? () => { cal.setSelectedDate(day); setSelectedSlot(null); } : undefined}
                            disabled={!hasSlots}
                            aria-pressed={isSel}
                            aria-label={`${format(day, 'd MMMM yyyy')}${hasSlots ? ', available' : ''}${isToday(day) ? ', today' : ''}`}
                          >
                            {day.getDate()}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className={styles.consultantCard}>
                    <div className={styles.consultant}>
                      <div className={styles.consultantAv}>
                        <PersonRegular fontSize={22} />
                      </div>
                      <div>
                        <div className={styles.consultantName}>Transformr Principal Consultant</div>
                        <div className={styles.consultantRole}>All times shown in AEST (Melbourne, Australia)</div>
                      </div>
                    </div>
                    <div className={styles.consultantFoot}>
                      <CalendarRegular fontSize={12} />
                      Available this week · 30 minute sessions
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 02 — choose a time (cards) */}
          {cal.selectedDate && cal.selectedDateSlots.length > 0 && (
            <div className={styles.numberedRow}>
              <div className={styles.num}>02</div>
              <div className={styles.rowContent}>
                <span className={styles.rowEyebrow}>Schedule · choose a time</span>
                <h2 className={styles.rowTitle}>{cal.dayHeaderFormatter.format(cal.selectedDate)}</h2>
                <Body1 className={styles.rowSubtitle}>
                  Every session runs 30 minutes. All times shown in AEST (Melbourne, Australia).
                </Body1>
                <div className={styles.timesGrid}>
                  {cal.selectedDateSlots.map((slot) => {
                    const sel = selectedSlot?.start === slot.start && selectedSlot?.staffUserId === slot.staffUserId;
                    return (
                      <button
                        key={`${slot.start}-${slot.staffUserId}`}
                        type="button"
                        className={mergeClasses(styles.timeChip, sel && styles.timeChipSelected)}
                        aria-pressed={sel}
                        onClick={() => { setSelectedSlot(slot); setError(null); }}
                      >
                        <span className={styles.timeChipH}>{cal.timeFormatter.format(new Date(slot.start))}</span>
                        <span className={styles.timeChipD}>30 min</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* 03 — attach reports (only if any exist) */}
          {reports.length > 0 && (
            <div className={styles.numberedRow}>
              <div className={styles.num}>03</div>
              <div className={styles.rowContent}>
                <span className={styles.rowEyebrow}>Schedule · attach reports (optional)</span>
                <h2 className={styles.rowTitle}>Reports to discuss</h2>
                <Body1 className={styles.rowSubtitle}>
                  Optionally attach up to {MAX_REPORTS} reports for your consultant to
                  review ({selectedReportIds.length} of {MAX_REPORTS} selected).
                </Body1>
                <div className={styles.repList}>
                  {reports.map((report) => {
                    const sel = selectedReportIds.includes(report.analysisId);
                    const disabled = !sel && selectedReportIds.length >= MAX_REPORTS;
                    return (
                      <button
                        key={report.analysisId}
                        type="button"
                        className={mergeClasses(styles.report, sel && styles.reportSelected)}
                        onClick={() => handleReportToggle(report.analysisId)}
                        disabled={disabled}
                        aria-pressed={sel}
                      >
                        <span className={mergeClasses(styles.repCheck, sel && styles.repCheckOn)}>
                          {sel && <CheckmarkRegular fontSize={12} />}
                        </span>
                        <span className={styles.repBody}>
                          <span className={styles.repName}>
                            {reportTitle(report)}
                          </span>
                          {(report.summary || report.aiGoals) && (
                            <span className={styles.repSub}>{report.summary || report.aiGoals}</span>
                          )}
                        </span>
                        <span className={styles.repDate}>
                          {report.storedAt ? reportDateFormatter.format(new Date(report.storedAt)) : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Add context */}
          <div className={styles.numberedRow}>
            <div className={styles.num}>{reports.length > 0 ? '04' : '03'}</div>
            <div className={styles.rowContent}>
              <span className={styles.rowEyebrow}>Schedule · add context</span>
              <h2 className={styles.rowTitle}>Anything you'd like to cover?</h2>
              <Body1 className={styles.rowSubtitle}>
                Optional — share topics or questions for your consultant.
              </Body1>
              <Textarea
                placeholder="Any notes or topics you'd like to discuss (optional)"
                value={notes}
                onChange={(_, data) => {
                  if (data.value.length <= 4000) setNotes(data.value);
                }}
                resize="vertical"
                rows={6}
                maxLength={4000}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div className={styles.actions} style={{ justifyContent: 'flex-end' }}>
            <Button
              appearance="primary"
              disabled={!selectedSlot}
              onClick={() => setStep('confirm')}
              icon={<CalendarRegular />}
            >
              Next
            </Button>
          </div>
        </>
      )}

      {/* Step 2: Confirm */}
      {step === 'confirm' && selectedSlot && (
        <div className={styles.section}>
          <Title3>Confirm your booking</Title3>

          <Card className={styles.summaryCard}>
            <div className={styles.summaryRow}>
              <Text className={styles.summaryLabel}>Time</Text>
              <Text className={styles.summaryValue}>
                {timeFormatter.format(new Date(selectedSlot.start))}
              </Text>
            </div>
            <div className={styles.summaryRow}>
              <Text className={styles.summaryLabel}>With</Text>
              <Text className={styles.summaryValue}>
                {selectedSlot.staffDisplayName}
              </Text>
            </div>
            <div className={styles.summaryRow}>
              <Text className={styles.summaryLabel}>Reports</Text>
              <div className={styles.summaryValue}>
                {selectedReportIds.length > 0
                  ? reports
                      .filter((r) => selectedReportIds.includes(r.analysisId))
                      .map((r) => (
                        <div key={r.analysisId}>
                          <Text>{r.fileName.replace(/\.docx$/i, '')}</Text>
                        </div>
                      ))
                  : <Text>None</Text>}
              </div>
            </div>
            {notes && (
              <div className={styles.summaryRow}>
                <Text className={styles.summaryLabel}>Notes</Text>
                <Text className={styles.summaryValue}>
                  {notes.length > 200 ? `${notes.slice(0, 200)}...` : notes}
                </Text>
              </div>
            )}
          </Card>

          <div className={styles.actions}>
            <Button
              appearance="subtle"
              icon={<ArrowLeftRegular />}
              onClick={() => setStep('select-time')}
              disabled={isSubmitting}
            >
              Back
            </Button>
            <Button
              appearance="primary"
              size="large"
              onClick={handleConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating meeting...' : 'Confirm Booking'}
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Success */}
      {step === 'success' && bookingResult && (
        <BookingConfirmation
          booking={bookingResult}
          timezone={timezone}
        />
      )}
      </div>
    </div>
  );
}
